import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Image,
  Badge,
  Button,
  Modal,
  Carousel,
  Tabs,
  Tab,
  Spinner,
  Form,
  InputGroup
} from 'react-bootstrap';
import { 
  FaCalendarAlt,
  FaUniversity,
  FaUsers,
  FaSearch,
  FaFilter,
  FaFileAlt,
  FaImages,
  FaDownload,
  FaHeart,
  FaShareAlt,
  FaInfoCircle,
  FaCalendarDay,
  FaCalendarWeek,
  FaMapMarkerAlt,
  FaChalkboardTeacher,
  FaLaptop,
  FaUsers as FaUsersGroup,
  FaCertificate,
  FaMicrophone,
  FaMusic,
  FaTheaterMasks,
  FaFutbol,
  FaUtensils,
  FaBook,
  FaGraduationCap,
  FaLightbulb,
  FaBriefcase,
  FaFlask,
  FaHeartbeat,
  FaHandsHelping,
  FaExclamationTriangle,
  FaArrowRight,
  FaEye
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';
import axios from 'axios';

// Event type icons and colors
const EVENT_TYPE_CONFIG = {
  'Seminar': {
    icon: <FaChalkboardTeacher />,
    color: '#1976d2',
    bgColor: 'rgba(25, 118, 210, 0.1)',
    gradient: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
    description: 'Educational presentations and discussions'
  },
  'Workshop': {
    icon: <FaLaptop />,
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    gradient: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
    description: 'Hands-on training sessions'
  },
  'Webinar': {
    icon: <FaMicrophone />,
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
    description: 'Online educational sessions'
  },
  'Technology & Innovation': {
    icon: <FaLightbulb />,
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
    gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
    description: 'Tech showcases and innovation events'
  },
  'Conferences': {
    icon: <FaUsersGroup />,
    color: '#e91e63',
    bgColor: 'rgba(233, 30, 99, 0.1)',
    gradient: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
    description: 'Academic and professional conferences'
  },
  'Skill-Building Activities': {
    icon: <FaCertificate />,
    color: '#00bcd4',
    bgColor: 'rgba(0, 188, 212, 0.1)',
    gradient: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
    description: 'Skill development workshops'
  },
  'Career Development': {
    icon: <FaBriefcase />,
    color: '#795548',
    bgColor: 'rgba(121, 85, 72, 0.1)',
    gradient: 'linear-gradient(135deg, #795548 0%, #5d4037 100%)',
    description: 'Career guidance and development'
  },
  'Research & Creative': {
    icon: <FaFlask />,
    color: '#3f51b5',
    bgColor: 'rgba(63, 81, 181, 0.1)',
    gradient: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
    description: 'Research presentations and creative works'
  },
  'Wellness & Personal': {
    icon: <FaHeartbeat />,
    color: '#009688',
    bgColor: 'rgba(0, 150, 136, 0.1)',
    gradient: 'linear-gradient(135deg, #009688 0%, #00796b 100%)',
    description: 'Health and personal development'
  },
  'Social & Cultural': {
    icon: <FaTheaterMasks />,
    color: '#ff5722',
    bgColor: 'rgba(255, 87, 34, 0.1)',
    gradient: 'linear-gradient(135deg, #ff5722 0%, #d84315 100%)',
    description: 'Cultural and social gatherings'
  },
  'Academic Support': {
    icon: <FaHandsHelping />,
    color: '#607d8b',
    bgColor: 'rgba(96, 125, 139, 0.1)',
    gradient: 'linear-gradient(135deg, #607d8b 0%, #455a64 100%)',
    description: 'Academic assistance programs'
  },
  'Other': {
    icon: <FaCalendarAlt />,
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
    gradient: 'linear-gradient(135deg, #757575 0%, #424242 100%)',
    description: 'Other academic events'
  }
};

const AcademicEvents = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'calendar'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'participants'
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming', 'past', 'all'

  // API configuration
  const API_URL = `${BASE_URL}/academic_events.php`;

  // Helper functions
  const getImageUrl = useCallback((path) => {
    if (!path) return null;
    if (path.startsWith('/')) {
      return `${UPLOAD_BASE_URL}${path}`;
    }
    return `${UPLOAD_BASE_URL}/${path}`;
  }, []);

  const getEventConfig = useCallback((eventType) => {
    return EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG['Other'];
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }, []);

  const formatDateRange = useCallback((dateFrom, dateTo) => {
    if (!dateFrom || !dateTo) return 'N/A';
    try {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      
      if (from.getTime() === to.getTime()) {
        return formatDate(dateFrom);
      }
      
      return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    } catch (error) {
      return 'Invalid Date Range';
    }
  }, [formatDate]);

  const isUpcoming = useCallback((dateTo) => {
    if (!dateTo) return false;
    try {
      const eventDate = new Date(dateTo);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate >= today;
    } catch (error) {
      return false;
    }
  }, []);

  const isFavorite = useCallback((eventId) => {
    return favorites.includes(eventId);
  }, [favorites]);

  const toggleFavorite = useCallback((eventId) => {
    setFavorites(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  }, []);

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setSelectedPhotoIndex(0);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setShowModal(false);
  };

  const handleShare = (event) => {
    if (navigator.share) {
      navigator.share({
        title: event.event_name,
        text: `Check out this ${event.event_type} event: ${event.event_name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Event link copied to clipboard!');
    }
  };

  const handleDownloadReport = (event) => {
    if (event.report_link) {
      const link = document.createElement('a');
      link.href = getImageUrl(event.report_link);
      link.download = event.report_link.split('/').pop() || 'event-report';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch events
      const eventsResponse = await axios.get(API_URL);
      
      if (eventsResponse.data.success && Array.isArray(eventsResponse.data.data)) {
        const eventsData = eventsResponse.data.data;
        
        // Process events
        const processedEvents = eventsData.map(event => {
          const config = getEventConfig(event.event_type);
          const upcoming = isUpcoming(event.date_to);
          
          return {
            ...event,
            id: parseInt(event.id),
            formattedDate: formatDate(event.date_from),
            formattedDateRange: formatDateRange(event.date_from, event.date_to),
            isUpcoming: upcoming,
            config: config,
            hasPhotos: event.photos && event.photos.length > 0,
            hasReport: !!event.report_link,
            participantsText: event.number_of_participants 
              ? `${event.number_of_participants} participants` 
              : 'Participants information not available'
          };
        });

        setEvents(processedEvents);
        
        // Extract academic years
        const yearsMap = new Map();
        eventsData.forEach(event => {
          if (event.academic_year ) {
            yearsMap.set(event.academic_year_id, {
              id: event.academic_year_id,
              name: event.academic_year ,
              count: 0
            });
          }
        });
        
        // Count events per year
        const years = Array.from(yearsMap.values());
        years.forEach(year => {
          year.count = processedEvents.filter(e => e.academic_year_id == year.id).length;
        });
        
        years.sort((a, b) => b.name.localeCompare(a.name));
        setAcademicYears(years);
        
        // Extract event types with counts
        const typesMap = new Map();
        Object.keys(EVENT_TYPE_CONFIG).forEach(type => {
          const count = processedEvents.filter(e => e.event_type === type).length;
          if (count > 0) {
            typesMap.set(type, {
              type: type,
              count: count,
              config: EVENT_TYPE_CONFIG[type]
            });
          }
        });
        
        setEventTypes(Array.from(typesMap.values()));
        
        // Get upcoming events
        const upcoming = processedEvents.filter(event => event.isUpcoming);
        setUpcomingEvents(upcoming.slice(0, 5));
        
      } else {
        setError('No academic events available');
        setEvents([]);
        setAcademicYears([]);
        setEventTypes([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load academic events. Please try again later.');
      setEvents([]);
      setAcademicYears([]);
      setEventTypes([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getEventConfig, isUpcoming, formatDate, formatDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort events
  useEffect(() => {
    let filtered = [...events];
    
    // Filter by academic year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(event => event.academic_year_id == selectedYear);
    }
    
    // Filter by event type
    if (selectedType !== 'all') {
      filtered = filtered.filter(event => event.event_type === selectedType);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.event_type && event.event_type.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by tab (upcoming/past)
    if (activeTab === 'upcoming') {
      filtered = filtered.filter(event => event.isUpcoming);
    } else if (activeTab === 'past') {
      filtered = filtered.filter(event => !event.isUpcoming);
    }
    
    // Sort events
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date_from) - new Date(a.date_from);
        case 'name':
          return a.event_name.localeCompare(b.event_name);
        case 'participants':
          return (b.number_of_participants || 0) - (a.number_of_participants || 0);
        default:
          return new Date(b.date_from) - new Date(a.date_from);
      }
    });
    
    setFilteredEvents(filtered);
  }, [events, selectedYear, selectedType, searchTerm, activeTab, sortBy]);

  const getFileIcon = (filePath) => {
    if (!filePath) return 'fa-file';
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      default:
        return 'fa-file';
    }
  };

  const getFileIconColor = (filePath) => {
    if (!filePath) return '#6c757d';
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '#dc3545';
      case 'doc':
      case 'docx':
        return '#0d6efd';
      case 'xls':
      case 'xlsx':
        return '#198754';
      case 'ppt':
      case 'pptx':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const getParticipantIcon = (count) => {
    if (!count) return <FaUsers className="text-muted" />;
    if (count < 50) return <FaUsers className="text-success" />;
    if (count < 200) return <FaUsers className="text-primary" />;
    return <FaUsers className="text-warning" />;
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <h4 className="mt-4" style={{ color: '#1a237e' }}>Loading Academic Events</h4>
          <p className="text-muted">Discovering campus activities and learning opportunities...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle className="me-3" size={24} />
            <div>
              <Alert.Heading>Unable to Load Events</Alert.Heading>
              <p>{error}</p>
              <Button variant="primary" onClick={fetchData}>
                Try Again
              </Button>
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        padding: '80px 0',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          opacity: 0.3
        }}></div>
        
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaGraduationCap className="me-2" /> Academic Events Portal
              </Badge>
              <h1 className="display-4 fw-bold mb-4">
                Discover Campus <span style={{ color: '#64ffda' }}>Learning Opportunities</span>
              </h1>
              <p className="lead mb-4">
                Explore seminars, workshops, conferences, and other academic events 
                designed to enhance your learning experience and professional development.
              </p>
              <div className="d-flex flex-wrap gap-4">
                <div>
                  <h2 className="mb-0">{events.filter(e => e.isUpcoming).length}</h2>
                  <small className="text-white-50">Upcoming Events</small>
                </div>
                <div>
                  <h2 className="mb-0">{events.length}</h2>
                  <small className="text-white-50">Total Events</small>
                </div>
                <div>
                  <h2 className="mb-0">{eventTypes.length}</h2>
                  <small className="text-white-50">Event Categories</small>
                </div>
                <div>
                  <h2 className="mb-0">{academicYears.length}</h2>
                  <small className="text-white-50">Academic Years</small>
                </div>
              </div>
            </Col>
            <Col lg={6} className="text-center">
              <div className="position-relative" style={{ height: '300px' }}>
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '20px',
                  width: '200px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  animation: 'float 6s ease-in-out infinite'
                }}>
                  <div style={{
                    fontSize: '32px',
                    color: '#1a237e',
                    marginBottom: '10px'
                  }}>
                    <FaChalkboardTeacher />
                  </div>
                  <h5 className="mb-2">Seminars & Workshops</h5>
                  <p className="small mb-0">Enhance your skills</p>
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '40px',
                  right: '100px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '20px',
                  width: '180px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  animation: 'float 6s ease-in-out infinite 2s'
                }}>
                  <div style={{
                    fontSize: '32px',
                    color: '#1a237e',
                    marginBottom: '10px'
                  }}>
                    <FaUsersGroup />
                  </div>
                  <h5 className="mb-2">Conferences</h5>
                  <p className="small mb-0">Network and learn</p>
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '100px',
                  right: '180px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '20px',
                  width: '160px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  animation: 'float 6s ease-in-out infinite 4s'
                }}>
                  <div style={{
                    fontSize: '32px',
                    color: '#1a237e',
                    marginBottom: '10px'
                  }}>
                    <FaFlask />
                  </div>
                  <h5 className="mb-2">Research Events</h5>
                  <p className="small mb-0">Explore innovations</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </div>

      {/* Search and Filter Section */}
      <Container className="mt-5">
        <Card className="border-0 shadow-lg" style={{ 
          borderRadius: '20px',
          marginTop: '-40px',
          position: 'relative',
          zIndex: 10
        }}>
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col md={6}>
                <InputGroup className="mb-3 mb-md-0">
                  <InputGroup.Text style={{ 
                    background: 'white', 
                    borderRight: 'none',
                    borderTopLeftRadius: '15px',
                    borderBottomLeftRadius: '15px'
                  }}>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search events by name or type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderLeft: 'none',
                      borderTopRightRadius: '15px',
                      borderBottomRightRadius: '15px',
                      padding: '15px'
                    }}
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                  <select
                    className="form-select"
                    style={{ maxWidth: '200px' }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="all">All Academic Years</option>
                    {academicYears.map(year => (
                      <option key={year.id} value={year.id}>
                        {year.name} ({year.count})
                      </option>
                    ))}
                  </select>
                  
                  <select
                    className="form-select"
                    style={{ maxWidth: '200px' }}
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="all">All Event Types</option>
                    {eventTypes.map(type => (
                      <option key={type.type} value={type.type}>
                        {type.type} ({type.count})
                      </option>
                    ))}
                  </select>
                  
                  <select
                    className="form-select"
                    style={{ maxWidth: '180px' }}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="date">Sort by Date</option>
                    <option value="name">Sort by Name</option>
                    <option value="participants">Sort by Participants</option>
                  </select>
                </div>
              </Col>
            </Row>
            
            {/* Tab Navigation */}
            <div className="mt-4">
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant={activeTab === 'upcoming' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setActiveTab('upcoming')}
                >
                  <FaCalendarDay className="me-2" />
                  Upcoming Events ({events.filter(e => e.isUpcoming).length})
                </Button>
                <Button
                  variant={activeTab === 'past' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setActiveTab('past')}
                >
                  <FaCalendarWeek className="me-2" />
                  Past Events ({events.filter(e => !e.isUpcoming).length})
                </Button>
                <Button
                  variant={activeTab === 'all' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setActiveTab('all')}
                >
                  <FaCalendarAlt className="me-2" />
                  All Events ({events.length})
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Event Categories Section */}
      <Container className="py-5">
        <h2 className="mb-4 text-center">Event Categories</h2>
        <p className="text-muted text-center mb-5">
          Browse events by category to find what interests you most
        </p>
        
        <Row className="g-4">
          {eventTypes.map(type => (
            <Col key={type.type} lg={3} md={4} sm={6}>
              <Card 
                className="border-0 shadow-sm h-100 cursor-pointer"
                onClick={() => setSelectedType(type.type)}
                style={{
                  transition: 'all 0.3s ease',
                  borderLeft: `4px solid ${type.config.color}`,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <Card.Body className="text-center p-4">
                  <div style={{
                    width: '70px',
                    height: '70px',
                    margin: '0 auto 20px',
                    background: type.config.gradient,
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '32px'
                  }}>
                    {type.config.icon}
                  </div>
                  <h5 className="mb-2">{type.type}</h5>
                  <p className="text-muted small mb-3">{type.config.description}</p>
                  <Badge 
                    bg={selectedType === type.type ? 'primary' : 'light'} 
                    text={selectedType === type.type ? 'white' : 'dark'}
                    className="px-3 py-2"
                  >
                    {type.count} Events
                  </Badge>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Events Section */}
      <Container className="pb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">
            {activeTab === 'upcoming' ? 'Upcoming Events' : 
             activeTab === 'past' ? 'Past Events' : 'All Events'}
          </h2>
          <div className="text-muted">
            Showing {filteredEvents.length} of {events.length} events
          </div>
        </div>

        {filteredEvents.length > 0 ? (
          <Row className="g-4">
            {filteredEvents.map((event) => (
              <Col key={event.id} lg={4} md={6}>
                <Card className="h-100 border-0 shadow-sm hover-shadow">
                  {/* Event Image */}
                  <div style={{
                    height: '200px',
                    background: event.config.gradient,
                    position: 'relative',
                    overflow: 'hidden',
                    borderTopLeftRadius: '15px',
                    borderTopRightRadius: '15px'
                  }}>
                    {event.hasPhotos ? (
                      <Image
                        src={getImageUrl(event.photos[0].photo_path)}
                        alt={event.event_name}
                        fluid
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.style.background = event.config.gradient;
                        }}
                      />
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white">
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>
                          {event.config.icon}
                        </div>
                        <h5 className="mb-0">{event.event_name}</h5>
                      </div>
                    )}
                    
                    {/* Event Type Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      padding: '5px 15px',
                      borderRadius: '20px',
                      fontWeight: 'bold',
                      color: event.config.color,
                      fontSize: '0.9rem'
                    }}>
                      {event.event_type}
                    </div>
                    
                    {/* Favorite Button */}
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        color: 'white',
                        fontSize: '1.2rem',
                        padding: '5px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => toggleFavorite(event.id)}
                    >
                      <FaHeart className={isFavorite(event.id) ? 'text-danger' : ''} />
                    </Button>
                  </div>
                  
                  <Card.Body className="p-4">
                    <Card.Title className="mb-3" style={{ color: '#1a237e' }}>
                      {event.event_name}
                    </Card.Title>
                    
                    <div className="d-flex align-items-center mb-3">
                      <FaCalendarAlt className="me-2" style={{ color: event.config.color }} />
                      <small className="text-muted">
                        {event.formattedDateRange}
                      </small>
                    </div>
                    
                    <div className="d-flex align-items-center mb-3">
                      <FaUniversity className="me-2" style={{ color: event.config.color }} />
                      <small className="text-muted">
                        {event.academic_year || 'Academic Event'}
                      </small>
                    </div>
                    
                    {event.number_of_participants && (
                      <div className="d-flex align-items-center mb-4">
                        {getParticipantIcon(event.number_of_participants)}
                        <small className="ms-2 text-muted">
                          {event.participantsText}
                        </small>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {event.hasReport && (
                          <Badge bg="info" className="me-2">
                            <FaFileAlt className="me-1" />
                            Report Available
                          </Badge>
                        )}
                        {event.hasPhotos && (
                          <Badge bg="success">
                            <FaImages className="me-1" />
                            {event.photos.length} Photos
                          </Badge>
                        )}
                      </div>
                      
                      <Button
                        variant="primary"
                        size="sm"
                        style={{ background: event.config.color, borderColor: event.config.color }}
                        onClick={() => handleViewDetails(event)}
                      >
                        <FaEye className="me-2" />
                        View Details
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <FaCalendarAlt size={48} className="text-muted mb-3" />
              <h4 className="mb-3">No Events Found</h4>
              <p className="text-muted mb-4">
                {searchTerm 
                  ? `No events match "${searchTerm}" for the selected filters`
                  : "No events are currently available for the selected category"}
              </p>
              <Button 
                variant="outline-primary"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedYear('all');
                  setSelectedType('all');
                  setActiveTab('all');
                }}
              >
                Show All Events
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Upcoming Events Sidebar Preview */}
        {upcomingEvents.length > 0 && activeTab !== 'upcoming' && (
          <div className="mt-5 pt-5">
            <h3 className="mb-4">Upcoming Events</h3>
            <Row className="g-3">
              {upcomingEvents.slice(0, 4).map(event => (
                <Col key={event.id} lg={3} md={6}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: event.config.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '18px',
                          marginRight: '15px'
                        }}>
                          {event.config.icon}
                        </div>
                        <div>
                          <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>
                            {event.event_name}
                          </h6>
                          <small className="text-muted d-block">
                            <FaCalendarAlt className="me-1" size={12} />
                            {event.formattedDate}
                          </small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Container>

      {/* Statistics Section */}
      <div style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '60px 0'
      }}>
        <Container>
          <h2 className="text-center mb-5">Event Statistics</h2>
          <Row className="g-4">
            <Col lg={3} md={6}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px'
                  }}>
                    <FaCalendarAlt />
                  </div>
                  <h2 className="mb-2">{events.length}</h2>
                  <p className="text-muted mb-0">Total Events</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px'
                  }}>
                    <FaChalkboardTeacher />
                  </div>
                  <h2 className="mb-2">{eventTypes.length}</h2>
                  <p className="text-muted mb-0">Event Categories</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px'
                  }}>
                    <FaUsers />
                  </div>
                  <h2 className="mb-2">
                    {events.reduce((sum, event) => sum + (event.number_of_participants || 0), 0).toLocaleString()}
                  </h2>
                  <p className="text-muted mb-0">Total Participants</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-4">
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                    borderRadius: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px'
                  }}>
                    <FaUniversity />
                  </div>
                  <h2 className="mb-2">{academicYears.length}</h2>
                  <p className="text-muted mb-0">Academic Years</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="event-detail-modal"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '15px',
                  background: selectedEvent.config.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '28px',
                  marginRight: '15px'
                }}>
                  {selectedEvent.config.icon}
                </div>
                <div>
                  <h3 className="mb-1">{selectedEvent.event_name}</h3>
                  <div className="d-flex align-items-center gap-3">
                    <Badge 
                      style={{ 
                        background: selectedEvent.config.bgColor, 
                        color: selectedEvent.config.color,
                        fontSize: '0.9rem',
                        padding: '5px 15px'
                      }}
                    >
                      {selectedEvent.event_type}
                    </Badge>
                    <small className="text-muted">
                      <FaCalendarAlt className="me-1" />
                      {selectedEvent.formattedDateRange}
                    </small>
                  </div>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-4">
            {/* Photo Carousel */}
            {selectedEvent.hasPhotos && (
              <div className="mb-4">
                <Carousel 
                  activeIndex={selectedPhotoIndex}
                  onSelect={setSelectedPhotoIndex}
                  interval={null}
                  indicators={selectedEvent.photos.length > 1}
                  controls={selectedEvent.photos.length > 1}
                >
                  {selectedEvent.photos.map((photo, index) => (
                    <Carousel.Item key={photo.id}>
                      <div style={{ 
                        height: '300px', 
                        overflow: 'hidden',
                        borderRadius: '12px'
                      }}>
                        <Image
                          src={getImageUrl(photo.photo_path)}
                          alt={photo.caption || selectedEvent.event_name}
                          fluid
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }}
                        />
                      </div>
                      {photo.caption && (
                        <Carousel.Caption className="bg-dark bg-opacity-50 py-2">
                          <small>{photo.caption}</small>
                        </Carousel.Caption>
                      )}
                    </Carousel.Item>
                  ))}
                </Carousel>
                
                {/* Thumbnail Strip */}
                {selectedEvent.photos.length > 1 && (
                  <div className="d-flex gap-2 mt-3 overflow-auto">
                    {selectedEvent.photos.map((photo, index) => (
                      <div 
                        key={photo.id}
                        style={{
                          width: '80px',
                          height: '60px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          opacity: index === selectedPhotoIndex ? 1 : 0.6,
                          border: index === selectedPhotoIndex ? `2px solid ${selectedEvent.config.color}` : 'none'
                        }}
                        onClick={() => setSelectedPhotoIndex(index)}
                      >
                        <Image
                          src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                          alt={`Thumbnail ${index + 1}`}
                          fluid
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Event Information */}
            <Row className="mb-4">
              <Col md={8}>
                <h5 className="mb-3">Event Information</h5>
                <div className="p-3 bg-light rounded mb-3">
                  <Row>
                    <Col md={6} className="mb-3">
                      <strong className="d-block text-muted">Academic Year</strong>
                      <span>{selectedEvent.academic_year}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block text-muted">Event Type</strong>
                      <Badge 
                        style={{ 
                          background: selectedEvent.config.bgColor, 
                          color: selectedEvent.config.color
                        }}
                      >
                        {selectedEvent.event_type}
                      </Badge>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block text-muted">Date Range</strong>
                      <span>{selectedEvent.formattedDateRange}</span>
                    </Col>
                    {selectedEvent.number_of_participants && (
                      <Col md={6} className="mb-3">
                        <strong className="d-block text-muted">Participants</strong>
                        <span>{selectedEvent.participantsText}</span>
                      </Col>
                    )}
                  </Row>
                </div>
              </Col>
              
              <Col md={4}>
                <Card className="border-0 bg-light">
                  <Card.Body className="p-3">
                    <h6 className="mb-3">Quick Stats</h6>
                    <div className="mb-3">
                      <small className="text-muted d-block">Photos</small>
                      <div className="fw-semibold">
                        {selectedEvent.hasPhotos ? `${selectedEvent.photos.length} photos` : 'No photos'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">Report</small>
                      <div className="fw-semibold">
                        {selectedEvent.hasReport ? 'Available' : 'Not available'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">Status</small>
                      <Badge bg={selectedEvent.isUpcoming ? 'success' : 'secondary'}>
                        {selectedEvent.isUpcoming ? 'Upcoming' : 'Completed'}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Report Section */}
            {selectedEvent.hasReport && (
              <div className="mb-4">
                <h5 className="mb-3">
                  <FaFileAlt className="me-2" />
                  Event Report
                </h5>
                <Card className="border">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <i 
                            className={`fas ${getFileIcon(selectedEvent.report_link)} fa-2x`}
                            style={{ color: getFileIconColor(selectedEvent.report_link) }}
                          ></i>
                        </div>
                        <div>
                          <h6 className="mb-1">
                            {selectedEvent.report_link.split('/').pop()}
                          </h6>
                          <small className="text-muted">
                            Click download to get the event report
                          </small>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        style={{ background: selectedEvent.config.color, borderColor: selectedEvent.config.color }}
                        onClick={() => handleDownloadReport(selectedEvent)}
                      >
                        <FaDownload className="me-2" />
                        Download Report
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Modal.Body>
          
          <Modal.Footer className="border-0">
            <div className="d-flex justify-content-between w-100">
              <div>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => toggleFavorite(selectedEvent.id)}
                >
                  <FaHeart className={isFavorite(selectedEvent.id) ? 'text-danger' : ''} />
                  <span className="ms-2">
                    {isFavorite(selectedEvent.id) ? 'Saved' : 'Save to Favorites'}
                  </span>
                </Button>
                <Button 
                  variant="outline-primary" 
                  className="ms-2"
                  onClick={() => handleShare(selectedEvent)}
                >
                  <FaShareAlt className="me-2" />
                  Share Event
                </Button>
              </div>
              <Button 
                variant="primary" 
                onClick={handleCloseModal}
                style={{ background: selectedEvent.config.color, borderColor: selectedEvent.config.color }}
              >
                Close Details
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AcademicEvents;